import React, { useEffect, useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HomeIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { t } from '../i18n';
import { withWeatherAgronomySearch } from '../utils/weatherAgronomyContext';
import { fieldWorkEntriesUrl, isFieldWorkEntryChildPath } from '../utils/fieldWorkOverviewNav';
import { preserveBusinessContextTo } from '../utils/businessContextNav';

const Breadcrumb = () => {
  const location = useLocation();
  const [breadcrumbs, setBreadcrumbs] = useState([]);

  // 使用useMemo缓存路径映射表，避免每次渲染都重新创建
  const pathMap = useMemo(() => ({
    // 欢迎页面
    '/welcome': t('nav_welcome'),
    '/smart-farm-cockpit': t('nav_smart_farm_cockpit'),
    '/smart-farm-cockpit/zone': '地块实时态势',

    // AI 智能服务
    '/ai-service': t('nav_plant_protection_inquiry'),
    '/plant-protection-inquiry/main': t('nav_disease_diagnosis'),
    '/plant-protection-inquiry/image': t('nav_diagnosis_image'),
    '/plant-protection-inquiry/text': t('nav_diagnosis_text'),
    '/plant-protection-inquiry/chat': t('nav_expert_diagnosis'),
    '/plant-protection-inquiry/history': t('nav_inquiry_history'),
    
    // 生产管理
    '/production': t('nav_my_plant_protection'),
    
    // 生产管理 - 种植计划
    '/production/crop/plans': t('nav_planted_crops'),
    '/production/crop/planted': t('nav_planted_crops'),
    '/production/crop/global-distribution': '作物全域分布管理',
    '/production/crop/growth-comparison': '作物长势全域对比',
    '/production/crop-model': t('nav_plant_growth_model'),
    '/production/land/hub': t('land_hub_title'),
    '/production/crop/growth-cycle': t('nav_growth_cycle'),
    '/production/crop/growth-budget': t('nav_growth_budget'),
    '/production/crop/warning-analysis': t('nav_warning_analysis'),
    
    // 生产管理 - 农资
    '/production/support/agricultural-materials': t('nav_agricultural_materials'),
    '/production/support/materials-info': t('nav_materials_info'),
    '/production/support/materials-management': t('nav_materials_management'),
    // 生产管理 - 采购
    '/production/support/farm-procurement': '采购管理',
    
    // 生产管理 - 农事作业
    '/production/field-work/plans': t('nav_field_management'),
    '/production/field-work': t('nav_field_work_overview'),
    '/production/field-work/ledger': '农事历史作业台账',
    '/production/crop/field-work-process': '作物标准农事流程',
    '/production/field-work/hub': '农事计划与任务',
    '/production/field-work/plan-manage': '农事计划',
    // 生产管理 - 营收统计
    '/production/land/business-analysis': t('page_title_data_statistics'),
    '/production/data-analysis': t('nav_data_analysis'),
    '/production/environment-monitoring': t('nav_environment_monitoring'),
    '/production/trend-analysis': t('nav_trend_analysis'),
    '/production/network-trends': t('nav_network_trends'),
    
    // 生产管理 - 资产管理
    '/production/land/asset-management': t('nav_asset_management'),
    '/production/asset-info': t('nav_asset_info'),
    '/production/asset-management-detail': t('nav_asset_management'),
    '/production/land/assets': t('land_hub_entry_survey'),
    '/production/land-detail': '地块详情',
    
    // 生产管理 - 其他
    '/production/field-work/pest-control': t('nav_disease_warning'),
    '/production/field-work/warning-management': t('nav_warning_management'),
    '/production/data-visualization': t('nav_data_visualization'),
    '/production/field-work/sowing': t('nav_sowing_management'),
    '/production/field-work/fertilizing': t('nav_fertilization_management'),
    '/production/field-work/machinery': '机械作业',
    '/production/field-work/irrigation': t('nav_irrigation_management'),
    '/production/irrigation-design-map': '灌溉设计地图',
    '/production/irrigation-equipment-catalog': '设备型号库',
    '/production/irrigation-design-rule-config': '规则配置',
    '/production/land': t('page_title_my_farm'),
    '/production/land/farm-management': t('my_farm_manage_plots'),
    '/production/land/irrigation-rule-config': '阈值规则配置',
    '/production/land/env-monitoring': '土壤墒情',
    '/production/land/irrigation-plan-ledger': '灌溉计划台账',
    '/production/field-work/pest-ecology': '病虫害与生态防治',
    '/production/crop/yield-analysis': '长势与产量分析',
    '/production/land/agri-cycle': '农资与循环',
    '/production/land/soil-testing': '土壤检测分析',
    '/production/land/fertility-analysis': '肥力分析',
    '/production/land/fertilizer-prescription': '施肥处方',
    '/production/land/fertilizer-delivery': '肥料专送',
    '/production/land/remote-sensing': '遥感检测',
    '/production/land/benefit-evaluation': t('page_title_data_statistics'),
    '/production/land/eco-agriculture': '生态农业',
    '/production/field-work/weeding': t('nav_weeding_management'),
    
    // 作物模型模块
    '/production/crop/models/list': t('nav_plant_growth_model'),
    '/production/crop/models/ai-modeling': t('nav_plant_growth_model'),
    '/production/field-work/calendar': '农事日历',
    
    // 种植计划模块
    '/plant-production-planning': t('nav_planting_plan'),
    '/plant-production-planning/annual-planning': t('nav_planting_plan'),
    '/plant-production-planning/crop-rotation': t('nav_planting_plan'),
    
    // 气象农艺模块
    '/weather-agronomy': t('nav_weather_agronomy'),
    '/weather-agronomy/data-crawling': t('nav_data_crawling'),
    '/weather-agronomy/data-analytics': t('nav_data_analytics'),
    '/weather-agronomy/disaster-warning': t('nav_disaster_warning'),
    '/weather-agronomy/weather-data': t('nav_weather_data'),
    '/weather-agronomy/local-climate': t('nav_local_climate'),
    '/weather-agronomy/intervention-plans': t('nav_intervention_plans'),
    '/weather-agronomy/planting-advice': t('ppn_planting_advice_title'),
    '/weather-agronomy/soil-analysis': '土壤依据',
    '/weather-agronomy/water-resources': '水情依据',
    '/weather-agronomy/historical-data': t('nav_historical_data'),
    '/weather-agronomy/trend-analysis': t('nav_trend_analysis'),
    
    // 知识库模块
    '/knowledge-base': t('nav_knowledge'),
    '/knowledge': t('nav_knowledge'),
    
    // 农业资讯
    '/agri-news': t('nav_plant_protection_news'),
    '/agri-news/news-center': t('nav_news_center'),
    '/agri-news/expert-advice': t('nav_expert_advice'),
    '/agri-news/seasonal-guidance': t('nav_seasonal_guidance'),
    '/agri-news/regional-news': t('nav_regional_news'),
    '/agri-news/research-progress': t('nav_research_progress'),
    '/agri-news/international-news': t('nav_international_news'),
    '/agri-news/pest-warning': t('ppn_pest_warning_title'),
    '/agri-news/market-analysis': t('ppn_market_analysis_title'),
    '/agri-news/data-statistics': t('ppn_data_statistics_title'),
    '/agri-news/network-trend-analysis': t('ppn_network_trend_analysis_title'),
    '/agri-news/water-resources': t('ppn_water_resources_title'),
    '/agri-news/soil-analysis': t('ppn_soil_analysis_title'),
    '/agri-news/planting-advice': t('ppn_planting_advice_title'),
    '/agri-news/safe-pesticide-use': t('ppn_safe_pesticide_use_title'),
    '/agri-news/environmental-protection': t('ppn_environmental_protection_title'),
    '/agri-news/technical-guidance': t('nav_technical_guidance'),
    '/agri-news/policy-interpretation': t('nav_policy_interpretation'),
    
    // 病虫害预警模块
    '/pest-warning': t('nav_pest_warning'),
    '/pest-warning/global-monitoring': t('nav_global_monitoring'),
    '/pest-warning/local-prevention': t('nav_local_prevention'),
    '/pest-warning/pest-disease': t('nav_pest_disease'),
    '/pest-warning/public-opinion': t('nav_public_opinion'),
    '/pest-warning/weather-disaster': t('nav_weather_disaster'),
    '/pest-warning/network-trend-analysis': t('nav_network_trend_analysis'),
    
    // 技术指导模块
    '/technical-guidance': t('nav_technical_guidance'),
    
    // 政策解读模块
    '/policy-interpretation': t('nav_policy_interpretation'),
    
    // 市场分析模块
    '/market-analysis': t('nav_market_analysis'),
    
    // 商城系统模块
    '/mall': t('nav_plant_protection_mall'),
    '/plant-protection-mall': t('nav_plant_protection_mall'),
    '/mall/customer-management': t('nav_customer_management'),
    '/mall/inventory-management': t('nav_inventory_management'),
    '/mall/logistics-management': t('nav_logistics_management'),
    '/mall/finance-management': t('nav_finance_management'),
    '/mall/vip-management': t('nav_vip_management'),
    '/mall/membership-management': t('nav_membership_management'),
    
    // 系统管理模块
    '/system-management': t('nav_system_management'),
    '/system-management/user-management': t('nav_user_management'),
    '/system-management/role-management': t('nav_role_management'),
    '/system-management/permission-management': t('nav_permission_management'),
    '/system-management/user-role': t('nav_user_role'),
    '/system-management/role-permission': t('nav_role_permission'),
    '/system-management/page-permission': t('nav_page_permission'),
    '/system-management/organization': t('nav_organization'),
    '/system-management/tenant-management': t('nav_tenant_management'),
    '/system-management/operation-log': t('nav_operation_log'),
    '/system-management/resource-monitoring': t('nav_resource_monitoring'),
    '/system-management/agricultural-material': t('nav_agricultural_material'),
    '/system-management/machine-management': t('nav_machine_management'),
    '/system-management/machine-rental': t('nav_machine_rental'),
    '/system-management/planting-resource': t('nav_planting_resource'),
    '/system-management/notification': t('nav_notification'),
    '/system-management/system-settings': t('nav_system_settings'),
    '/system-management/cache-management': t('nav_cache_management'),
    '/system-management/ai-settings': t('nav_ai_settings'),
    '/system-management/database-health-check': t('nav_database_health_check'),
    '/system-management/dict-management': t('nav_dict_management'),
    '/system-management/experience-review': '经验共创审核',
    '/department': t('breadcrumb_department_management'),
    '/system-management/security-settings': t('nav_security_settings'),
    '/system-management/mall-management': t('nav_mall_management'),
    
    // 统计分析模块
    '/statistics': t('nav_statistics'),
    '/statistics/asset-statistics': t('nav_asset_statistics'),
    '/statistics/output-statistics': t('nav_output_statistics'),
    '/statistics/performance-tracking': t('nav_performance_tracking'),
    '/statistics/economic-analysis': t('nav_economic_analysis'),
    '/statistics/planning-statistics': t('nav_planning_statistics'),
    '/statistics/data-statistics': t('nav_data_statistics'),
    
    // 个人中心模块
    '/profile': t('nav_profile'),
    '/profile/account-settings': t('nav_account_settings'),
    '/profile/my-interactions': t('my_interactions'),
    '/profile/my-comments': t('nav_my_comments'),
    '/profile/my-favorites': t('nav_my_favorites'),
    '/profile/my-likes': t('nav_my_likes'),
    '/profile/my-orders': t('nav_my_orders'),
    '/profile/my-shares': t('nav_my_shares'),
    '/profile/membership': t('nav_membership'),
    
    // 农资管理模块
    '/agricultural-material': t('nav_agricultural_material'),
    '/agricultural-material/category-management': t('nav_agricultural_material'),
    '/agricultural-material/inventory-management': t('nav_inventory_management'),
    
    // 设备管理模块
    '/equipment-management': t('nav_machine_management'),
    
    // 资源监控模块
    '/resource-monitoring': t('nav_resource_monitoring_system'),
    
    // 操作日志模块
    '/operation-log': t('nav_operation_log'),
    
    // 规划统计模块
    '/planning-statistics': t('nav_planning_statistics'),
    
    // 网络数据模块
    '/network-data': t('nav_data_crawling'),
    
    // 资产统计模块
    '/asset-statistics': t('nav_asset_statistics'),
    
    // 数据统计模块
    '/data-statistics': t('nav_data_statistics'),
    
    // 客户统计模块
    '/customer-statistics': t('nav_customer_management'),
    
    // 财务统计模块
    '/financial-statistics': t('nav_finance_management'),
    
    // 库存统计模块
    '/inventory-statistics': t('nav_inventory_management'),
    
    // 物流统计模块
    '/logistics-statistics': t('nav_logistics_management'),
    
    // 商城统计模块
    '/mall-statistics': t('nav_mall_management')
  }), []);

  // 详情页路径映射：详情页路径 -> 父路径标签
  const detailPageMap = useMemo(() => ({
    'news-detail': t('nav_news_center'),
    'research-progress': t('nav_research_progress'),
    'market-analysis': t('ppn_market_analysis_title'),
    'data-statistics': t('ppn_data_statistics_title'),
    'seasonal-guidance': t('nav_seasonal_guidance')
  }), [t]);

  useEffect(() => {
    const generateBreadcrumbs = () => {
      const newBreadcrumbs = [];
      const pathSegments = location.pathname.split('/').filter(Boolean);

      // 首页
      newBreadcrumbs.push({
        path: '/',
        label: t('nav_home'),
        isActive: false
      });

      // 地块地图：定制面包屑（避免回退到 页面1/2/3）
      // 路由：/asset-management/land-map/:assetId
      if (/^\/asset-management\/land-map\/[^/]+/.test(location.pathname)) {
        newBreadcrumbs.push(
          { path: '/production', label: t('nav_my_plant_protection'), isActive: false },
          { path: '/production/land/hub', label: t('land_hub_title'), isActive: false },
          { path: '/production/land/assets', label: t('land_hub_entry_survey'), isActive: false },
          { path: location.pathname, label: '地块信息', isActive: true }
        );
        setBreadcrumbs(newBreadcrumbs);
        return;
      }

      // 气象农艺：首页 > 气象农艺 > 分组 > 子页
      if (
        location.pathname === '/weather-agronomy'
        || location.pathname.startsWith('/weather-agronomy/')
      ) {
        const groupByLeaf = {
          'weather-data': { title: '气象监测', path: '/weather-agronomy/weather-data' },
          'local-climate': { title: '气象监测', path: '/weather-agronomy/weather-data' },
          'disaster-warning': { title: '气象监测', path: '/weather-agronomy/weather-data' },
          'intervention-plans': { title: '决策建议', path: '/weather-agronomy/intervention-plans' },
          'planting-advice': { title: '决策建议', path: '/weather-agronomy/intervention-plans' },
          'soil-analysis': { title: '资源依据', path: '/weather-agronomy/soil-analysis' },
          'water-resources': { title: '资源依据', path: '/weather-agronomy/soil-analysis' }
        };
        const leaf = pathSegments[1];
        const leafLabel = leaf
          ? (pathMap[`/weather-agronomy/${leaf}`] || leaf)
          : null;
        const group = leaf ? groupByLeaf[leaf] : null;
        const waSearch = location.search || '';

        newBreadcrumbs.push({
          path: withWeatherAgronomySearch('/weather-agronomy/weather-data', waSearch),
          label: t('nav_weather_agronomy'),
          isActive: !leaf
        });
        if (group) {
          newBreadcrumbs.push({
            path: withWeatherAgronomySearch(group.path, waSearch),
            label: group.title,
            isActive: false
          });
        }
        if (leaf && leafLabel) {
          newBreadcrumbs.push({
            path: `${location.pathname}${waSearch}`,
            label: leafLabel,
            isActive: true
          });
        }
        setBreadcrumbs(newBreadcrumbs);
        return;
      }

      // 处理其他路径
      if (pathSegments.length > 0) {
        let currentPath = '';
        
        pathSegments.forEach((segment, index) => {
          currentPath += `/${segment}`;
          
          // 检查是否是ID段（数字或UUID），如果是，则显示"详情"
          const isNumericId = /^\d+$/.test(segment);
          const isUuidId = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(segment)
            || /^[0-9a-f]{24,36}$/i.test(segment);
          let label;
          
          if ((isNumericId || isUuidId) && index > 0) {
            // 这是ID段，显示"详情"
            label = '详情';
          } else if (detailPageMap[segment]) {
            // 这是详情页路径段（如 news-detail），使用对应的父路径标签
            label = detailPageMap[segment];
          } else {
            // 普通路径，从pathMap中查找
            label = pathMap[currentPath] || t('nav_page').replace('{number}', index + 1);
          }

          let linkPath = currentPath;
          if (
            currentPath === '/production/field-work'
            && isFieldWorkEntryChildPath(location.pathname)
            && index < pathSegments.length - 1
          ) {
            linkPath = fieldWorkEntriesUrl();
          }
          // 面包屑上级链路透传 farmId/landId/deviceId，与菜单/返回口径一致
          if (
            index < pathSegments.length - 1
            && (
              currentPath.startsWith('/production')
              || currentPath.startsWith('/smart-farm-cockpit')
            )
          ) {
            linkPath = preserveBusinessContextTo(linkPath, location);
          }
          
          newBreadcrumbs.push({
            path: linkPath,
            label: label,
            isActive: index === pathSegments.length - 1
          });
        });
      }

      setBreadcrumbs(newBreadcrumbs);
    };

    generateBreadcrumbs();
  }, [location.pathname, location.search, pathMap, detailPageMap, t]); // 添加依赖

  // 如果只有一个面包屑项（首页），不显示面包屑
  if (breadcrumbs.length <= 1) {
    return null;
  }

  return (
    <nav className="flex w-max max-w-full flex-nowrap items-center gap-0.5 text-xs text-gray-500 dark:text-gray-400">
      {breadcrumbs.map((breadcrumb, index) => (
        <React.Fragment key={breadcrumb.path + '-' + index}>
          {index > 0 && (
            <ChevronRightIcon className="w-3 h-3 shrink-0 mx-0.5 text-gray-400 dark:text-gray-500" />
          )}
          <Link
            to={breadcrumb.path}
            className={`hover:text-blue-600 dark:hover:text-blue-400 transition-colors inline-flex shrink-0 items-center whitespace-nowrap px-1.5 py-0.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer ${
              breadcrumb.isActive ? 'text-blue-600 dark:text-blue-400 font-medium' : ''
            }`}
          >
            {index === 0 ? (
              <HomeIcon className="w-3 h-3 mr-1 shrink-0" />
            ) : null}
            {breadcrumb.label}
          </Link>
        </React.Fragment>
      ))}
    </nav>
  );
};

export default Breadcrumb;

